import React from "react";
import PropTypes from "prop-types";
import { Loader, Tooltip, Whisper } from "rsuite";
import ExpandIcon from "../../assets/img/expand-icon.png";
import InfoIcon from "../../assets/img/info.svg";
import { dateFormatter } from "../../utils/dateUtils";
import { DAILY_RATES_STATUS } from "./constants";
import { buildDailyBreakdownSummary, formatDailyRateAmount } from "./utils";

const ReservationDailyBreakdown = ({ rowData, dailyRatesEntry }) => {
  const status = dailyRatesEntry?.status || DAILY_RATES_STATUS.LOADING;
  const rates = dailyRatesEntry?.rates || [];
  const currency = rates[0]?.payableCurrency || rowData.currency;

  // Keyed by the summary's grouping key, not the label: two tax codes can
  // share a display name, and duplicate React keys mis-reuse rows.
  const renderTaxLineRows = (lines) =>
    lines.map((line) => (
      <tr key={line.key}>
        <td>{line.label}</td>
        <td>{formatDailyRateAmount(line.amount, currency)}</td>
      </tr>
    ));

  const renderBody = () => {
    if (status === DAILY_RATES_STATUS.LOADING) {
      return <Loader content="Loading daily breakdown..." />;
    }
    if (status === DAILY_RATES_STATUS.ERROR) {
      return (
        <p className="res-daily-breakdown__status">
          Failed to load the daily breakdown. Collapse and expand the row to
          retry.
        </p>
      );
    }
    if (!rates.length) {
      return (
        <p className="res-daily-breakdown__status">
          No daily breakdown is available for this reservation.
        </p>
      );
    }
    const summary = buildDailyBreakdownSummary(rates);
    return (
      <div className="res-daily-breakdown__table-wrapper">
        <table className="res-daily-breakdown__table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Room price</th>
            </tr>
          </thead>
          <tbody>
            {rates.map((rate) => (
              <tr key={`daily-rate-${rate.date}`}>
                <td>{dateFormatter(rate.date)}</td>
                <td>{formatDailyRateAmount(rate.payableNetPrice, currency)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th>Net Price</th>
              <th>{formatDailyRateAmount(summary.netTotal, currency)}</th>
            </tr>
            {renderTaxLineRows(summary.bookingLines)}
            {renderTaxLineRows(summary.propertyLines)}
            <tr className="total-row">
              <th>Total</th>
              <th>{formatDailyRateAmount(summary.grandTotal, currency)}</th>
            </tr>
            <tr>
              <td>Pay at time of booking</td>
              <td>
                {formatDailyRateAmount(summary.payAtBookingTotal, currency)}
              </td>
            </tr>
            <tr>
              <td>
                Pay at property
                <Whisper
                  placement="topStart"
                  speaker={
                    <Tooltip className="summary-tooltip res-daily-breakdown__tooltip">
                      Taxes and fees marked pay-at-property are collected
                      locally by the hotel
                    </Tooltip>
                  }
                >
                  <img
                    src={InfoIcon}
                    alt="info"
                    className="res-daily-breakdown__info-icon"
                  />
                </Whisper>
              </td>
              <td>
                {formatDailyRateAmount(summary.payAtPropertyTotal, currency)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  };

  return (
    <div className="res-daily-breakdown">
      <div className="res-daily-breakdown__header">
        <img
          src={ExpandIcon}
          alt=""
          className="res-daily-breakdown__header-icon"
        />
        <span className="res-daily-breakdown__header-text">
          Daily breakdown — {rowData.htConfirmationCode}
        </span>
      </div>
      {renderBody()}
    </div>
  );
};

ReservationDailyBreakdown.propTypes = {
  rowData: PropTypes.shape({
    htConfirmationCode: PropTypes.string,
    currency: PropTypes.string,
  }).isRequired,
  dailyRatesEntry: PropTypes.shape({
    status: PropTypes.oneOf(Object.values(DAILY_RATES_STATUS)),
    rates: PropTypes.arrayOf(
      PropTypes.shape({
        date: PropTypes.string,
        payableNetPrice: PropTypes.number,
        payableTax: PropTypes.number,
        payableCurrency: PropTypes.string,
        taxBreakup: PropTypes.object,
      })
    ),
  }),
};

export default ReservationDailyBreakdown;
